import type { AnalysisRow, DatabankAnalysis, ExtremeRow } from "../api/databankApi";

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */
export const naira = (n: any) =>
  `₦${Number(n || 0).toLocaleString("en-NG", { maximumFractionDigits: 2 })}`;

function pct(part: number, total: number) {
  if (!total || !part) return "0.00%";
  return ((part / total) * 100).toFixed(2) + "%";
}

/* ------------------------------------------------------------------ */
/* Print — a clean popup, so the page's scroll containers can't clip it */
/* ------------------------------------------------------------------ */
export function printRef(el: HTMLDivElement | null, title: string) {
  if (!el) return;

  const pw = window.open("", "_blank");
  if (!pw) {
    alert("Pop-ups are blocked. Please allow pop-ups for this site to print.");
    return;
  }

  const styleContent = Array.from(document.head.querySelectorAll("style"))
    .map((s) => `<style>${s.textContent}</style>`)
    .join("\n");

  const linkContent = Array.from(
    document.head.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'),
  )
    .map((l) => `<link rel="stylesheet" href="${l.href}">`)
    .join("\n");

  pw.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${title}</title>
  ${linkContent}
  ${styleContent}
  <style>
    body { background: white !important; padding: 32px; font-family: sans-serif; }
    @page  { margin: 1.5cm; }
    table  { page-break-inside: auto; border-collapse: collapse; width: 100%; }
    tr     { page-break-inside: avoid; page-break-after: auto; }
    thead  { display: table-header-group; }
    .no-print { display: none !important; }
  </style>
</head>
<body>${el.innerHTML}</body>
</html>`);

  pw.document.close();
  pw.focus();
  pw.onload = () => pw.print();
  setTimeout(() => { try { pw.print(); } catch (_) {} }, 900);
}

/* ------------------------------------------------------------------ */
/* Table atoms                                                          */
/* ------------------------------------------------------------------ */
function SectionHeader({ num, title, columns }: { num: string; title: string; columns: string[] }) {
  return (
    <>
      <tr>
        <td colSpan={columns.length} className="pt-8 pb-0">
          <div className="flex items-stretch overflow-hidden rounded-t-xl shadow-sm">
            <div className="bg-gray-900 text-white text-[15px] font-black px-5 py-3.5 flex items-center shrink-0 min-w-[56px] justify-center">
              {num}
            </div>
            <div className="bg-gray-800 text-white flex-1 px-5 py-3.5 flex items-center">
              <span className="text-[13px] font-black uppercase tracking-[0.15em]">{title}</span>
            </div>
          </div>
        </td>
      </tr>
      <tr className="bg-gray-100 border-x border-gray-200">
        {columns.map((c, i) => (
          <th
            key={c + i}
            // The first column carries the labels and takes the slack; the
            // rest are figures and need only enough room for a number.
            className={`px-2 sm:px-4 py-2.5 text-[10px] font-black uppercase tracking-widest text-gray-500 ${
              i === 0 ? "text-left" : "w-[14%] text-right"
            }`}
          >
            {c}
          </th>
        ))}
      </tr>
    </>
  );
}

function NoDataRow({ span }: { span: number }) {
  return (
    <tr className="border-x border-b border-gray-100 bg-gray-50/30">
      <td colSpan={span} className="px-4 py-5 text-center text-[12px] text-gray-400 italic font-medium">
        No data available for this selection
      </td>
    </tr>
  );
}

function SectionEnd({ span }: { span: number }) {
  return <tr><td colSpan={span} className="border-x border-b border-gray-200 rounded-b-xl h-1 bg-white" /></tr>;
}

function Spacer({ span }: { span: number }) {
  return <tr><td colSpan={span} className="h-3" /></tr>;
}

/** A cell of plain text / numbers. */
function Cell({ children, align = "right", strong }: { children: any; align?: "left" | "right"; strong?: boolean }) {
  return (
    <td
      className={`px-2 sm:px-4 py-3 align-top text-[13px] tabular-nums ${
        align === "left" ? "text-left text-gray-800" : "text-right text-gray-900"
      } ${strong ? "font-black" : "font-medium"}`}
    >
      {children}
    </td>
  );
}

/** The share-of-total bar, reused wherever a count is a proportion. */
function ShareBar({ value, total }: { value: number; total: number }) {
  const p = total ? (value / total) * 100 : 0;
  const color = p >= 50 ? "bg-indigo-600" : p >= 25 ? "bg-indigo-500" : p >= 10 ? "bg-indigo-400" : "bg-indigo-300";
  return (
    <td className="px-2 sm:px-4 py-3 align-top">
      <div className="flex items-center gap-2">
        <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden min-w-[32px]">
          <div className={`h-full ${color} rounded-full transition-all duration-500`} style={{ width: `${Math.min(100, p)}%` }} />
        </div>
        <span className="text-[11px] font-black text-gray-500 w-14 text-right shrink-0 tabular-nums">
          {pct(value, total)}
        </span>
      </div>
    </td>
  );
}

/* ------------------------------------------------------------------ */
/* Count + money breakdown (by state / category / unit / year / month) */
/* ------------------------------------------------------------------ */
function BreakdownSection({
  num, title, rows, total, limit,
}: {
  num: string; title: string; rows: AnalysisRow[]; total: number; limit?: number;
}) {
  const shown = limit ? rows.slice(0, limit) : rows;
  const cols = ["Description", "Records", "Average", "Lowest", "Highest", "Share of records"];

  return (
    <>
      <SectionHeader num={num} title={title} columns={cols} />
      {shown.length === 0 ? <NoDataRow span={cols.length} /> : (
        shown.map((r) => (
          <tr key={r.label} className="border-x border-b border-gray-100 even:bg-gray-50/40 hover:bg-indigo-50/30 transition-colors">
            <Cell align="left" strong>{r.label}</Cell>
            <Cell strong>{r.count.toLocaleString()}</Cell>
            <Cell>{naira(r.average)}</Cell>
            <Cell>{naira(r.min)}</Cell>
            <Cell>{naira(r.max)}</Cell>
            <ShareBar value={r.count} total={total} />
          </tr>
        ))
      )}
      {limit && rows.length > limit && (
        <tr className="border-x border-b border-gray-100 bg-gray-50/60">
          <td colSpan={cols.length} className="px-4 py-2 text-[11px] text-gray-400 font-semibold italic">
            Showing the top {limit} of {rows.length.toLocaleString()} — narrow the filters above to see the rest.
          </td>
        </tr>
      )}
      <SectionEnd span={cols.length} />
      <Spacer span={cols.length} />
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Dearest / cheapest entries                                          */
/* ------------------------------------------------------------------ */
function ExtremesSection({ num, title, rows }: { num: string; title: string; rows: ExtremeRow[] }) {
  const cols = ["Item", "Unit", "Category", "State", "Period", "Price"];
  return (
    <>
      <SectionHeader num={num} title={title} columns={cols} />
      {rows.length === 0 ? <NoDataRow span={cols.length} /> : (
        rows.map((r, i) => (
          <tr key={`${r.description}-${i}`} className="border-x border-b border-gray-100 even:bg-gray-50/40 hover:bg-indigo-50/30 transition-colors">
            <Cell align="left" strong>{r.description}</Cell>
            <Cell align="left">{r.unit}</Cell>
            <Cell align="left">{r.category}</Cell>
            <Cell align="left">{r.state}</Cell>
            <Cell align="left">{r.period}</Cell>
            <Cell strong>{naira(r.price)}</Cell>
          </tr>
        ))
      )}
      <SectionEnd span={cols.length} />
      <Spacer span={cols.length} />
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Stat cards                                                           */
/* ------------------------------------------------------------------ */
function StatCard({ label, value, sub, color }: { label: string; value: string; sub?: string; color: string }) {
  return (
    <div className={`flex-1 min-w-[140px] rounded-2xl p-4 border ${color}`}>
      <p className="text-[10px] font-black uppercase tracking-widest opacity-70 mb-1">{label}</p>
      <p className="text-2xl font-black leading-none break-words">{value}</p>
      {sub && <p className="text-[10px] font-bold opacity-60 mt-1">{sub}</p>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* The report                                                           */
/* ------------------------------------------------------------------ */
export default function DatabankAnalysisReport({
  data, title, filterChips,
}: {
  data?: DatabankAnalysis;
  title: string;
  filterChips: Array<{ label: string; value: string }>;
}) {
  const total = data?.meta?.total ?? 0;
  const s = data?.summary;
  const ps = data?.price_stats;

  const generatedAt = data?.meta?.generated_at
    ? new Date(data.meta.generated_at).toLocaleString("en-GB", {
        day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
      })
    : new Date().toLocaleString("en-GB", { day: "numeric", month: "long", year: "numeric" });

  const period =
    s?.earliest_year && s?.latest_year
      ? s.earliest_year === s.latest_year ? String(s.earliest_year) : `${s.earliest_year} – ${s.latest_year}`
      : "—";

  const bandEntries = Object.entries(data?.price_bands ?? {});
  const bandTotal = bandEntries.reduce((a, [, v]) => a + v, 0);

  const COLS = 6;

  return (
    <div className="font-sans text-gray-900">
      {/* ── Document header ── */}
      <div className="text-center pb-6 border-b-4 border-gray-900 mb-1">
        <p className="text-[10px] font-black uppercase tracking-[0.25em] text-gray-400 mb-2">
          Nigerian Institute of Quantity Surveyors — NIQS
        </p>
        <h1 className="text-[19px] font-black uppercase tracking-wide text-gray-900 leading-tight">
          Analysis of the Cost Databank
        </h1>
        <p className="text-[14px] font-bold text-indigo-700 mt-1">{title}</p>
        <p className="text-[10px] text-gray-400 mt-1.5 font-semibold">Generated: {generatedAt}</p>
        {filterChips.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2 justify-center">
            {filterChips.map((c) => (
              <span key={c.label + c.value} className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 border border-indigo-100 rounded-full text-[9px] font-bold text-indigo-700 uppercase tracking-wider">
                {c.label}: {c.value}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ── Summary cards ── */}
      <div className="flex flex-wrap gap-3 my-5">
        <StatCard label="Price Records" value={total.toLocaleString()} sub={`${(s?.distinct_items ?? 0).toLocaleString()} distinct items`} color="bg-gray-900 text-white border-gray-900" />
        <StatCard label="Median Price"  value={naira(ps?.median)} sub={`Average ${naira(ps?.average)}`} color="bg-indigo-50 text-indigo-900 border-indigo-200" />
        <StatCard label="Price Range"   value={`${naira(ps?.min)} – ${naira(ps?.max)}`} sub="Lowest to highest recorded" color="bg-amber-50 text-amber-900 border-amber-200" />
        <StatCard label="States Covered" value={`${s?.states_covered ?? 0} / ${s?.states_total ?? 0}`} sub={`${(data?.uncovered_states?.length ?? 0)} with no prices`} color="bg-green-50 text-green-900 border-green-200" />
        <StatCard label="Period"        value={period} sub={`${s?.categories_covered ?? 0} categories`} color="bg-blue-50 text-blue-900 border-blue-200" />
      </div>

      {total === 0 ? (
        <div className="py-20 text-center border-2 border-dashed border-gray-200 rounded-2xl">
          <p className="font-bold text-gray-700">Nothing matches these filters</p>
          <p className="text-sm text-gray-400 mt-1">
            The databank holds no price records for this selection. Widen the scope above.
          </p>
        </div>
      ) : (
        <table
          // `table-fixed` keeps this inside its container on a narrow screen:
          // cells wrap onto a second line rather than widening the table until
          // the page scrolls sideways.
          className="w-full table-fixed border-collapse break-words"
        >
          <tbody>

            {/* ── 1. Price statistics ── */}
            <SectionHeader num="1" title="Price Statistics" columns={["Measure", "Value", "", "", "", ""]} />
            {[
              ["Records priced", ps?.count?.toLocaleString() ?? "0", "How many rows the figures below are drawn from"],
              ["Lowest price", naira(ps?.min), "The cheapest entry in the selection"],
              ["Lower quartile (25%)", naira(ps?.p25), "A quarter of entries fall below this"],
              ["Median price", naira(ps?.median), "The middle entry — the figure to trust when prices are skewed"],
              ["Average price", naira(ps?.average), "The mean; pulled upward by a few very large entries"],
              ["Upper quartile (75%)", naira(ps?.p75), "Three quarters of entries fall below this"],
              ["Highest price", naira(ps?.max), "The dearest entry in the selection"],
            ].map(([label, value, note]) => (
              <tr key={label as string} className="border-x border-b border-gray-100 even:bg-gray-50/40">
                <Cell align="left" strong>{label}</Cell>
                <Cell strong>{value}</Cell>
                <td colSpan={4} className="px-4 py-3 text-[11px] text-gray-400 font-medium italic">{note}</td>
              </tr>
            ))}
            <SectionEnd span={COLS} /><Spacer span={COLS} />

            {/* ── 2. Price bands ── */}
            <SectionHeader num="2" title="Distribution by Price Band" columns={["Band", "Records", "", "", "", "Share of records"]} />
            {bandEntries.length === 0 ? <NoDataRow span={COLS} /> : bandEntries.map(([label, count]) => (
              <tr key={label} className="border-x border-b border-gray-100 even:bg-gray-50/40 hover:bg-indigo-50/30 transition-colors">
                <Cell align="left" strong>{label}</Cell>
                <Cell strong>{count.toLocaleString()}</Cell>
                <td colSpan={3} />
                <ShareBar value={count} total={bandTotal} />
              </tr>
            ))}
            <SectionEnd span={COLS} /><Spacer span={COLS} />

            {/* ── 3–8. Count + money breakdowns ── */}
            <BreakdownSection num="3" title="Prices by State"        rows={data?.by_state ?? []}        total={total} />
            <BreakdownSection num="4" title="Prices by Category"     rows={data?.by_category ?? []}     total={total} />
            <BreakdownSection num="5" title="Prices by Sub-Category" rows={data?.by_sub_category ?? []} total={total} />
            <BreakdownSection num="6" title="Prices by Unit of Measure" rows={data?.by_unit ?? []}      total={total} limit={20} />
            <BreakdownSection num="7" title="Prices by Year"         rows={data?.by_year ?? []}         total={total} />
            <BreakdownSection num="8" title="Prices by Month"        rows={data?.by_month ?? []}        total={total} />

            {/* ── 9. Widest price spread between states ── */}
            <SectionHeader
              num="9"
              title="Widest Price Spread Between States"
              columns={["Item", "Unit", "States", "Lowest", "Highest", "Spread"]}
            />
            {(data?.widest_spread ?? []).length === 0 ? <NoDataRow span={COLS} /> : (
              (data?.widest_spread ?? []).map((r, i) => (
                <tr key={`${r.label}-${i}`} className="border-x border-b border-gray-100 even:bg-gray-50/40 hover:bg-indigo-50/30 transition-colors">
                  <Cell align="left" strong>{r.label}</Cell>
                  <Cell align="left">{r.unit}</Cell>
                  <Cell>{r.states}</Cell>
                  <Cell>{naira(r.min)}</Cell>
                  <Cell>{naira(r.max)}</Cell>
                  <td className="px-2 sm:px-4 py-3 align-top text-right">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-amber-50 border border-amber-200 text-[12px] font-black text-amber-800 tabular-nums">
                      +{r.spread_pct.toLocaleString()}%
                    </span>
                  </td>
                </tr>
              ))
            )}
            <tr className="border-x border-b border-gray-100 bg-gray-50/60">
              <td colSpan={COLS} className="px-4 py-2 text-[11px] text-gray-400 font-semibold italic">
                Items priced in at least three states, ranked by how far the highest sits above the lowest.
              </td>
            </tr>
            <SectionEnd span={COLS} /><Spacer span={COLS} />

            {/* ── 10. Most tracked items ── */}
            <SectionHeader num="10" title="Most Frequently Priced Items" columns={["Item", "Records", "Average", "States", "", "Share of records"]} />
            {(data?.most_tracked ?? []).length === 0 ? <NoDataRow span={COLS} /> : (
              (data?.most_tracked ?? []).map((r, i) => (
                <tr key={`${r.label}-${i}`} className="border-x border-b border-gray-100 even:bg-gray-50/40 hover:bg-indigo-50/30 transition-colors">
                  <Cell align="left" strong>{r.label}</Cell>
                  <Cell strong>{r.count.toLocaleString()}</Cell>
                  <Cell>{naira(r.average)}</Cell>
                  <Cell>{r.states}</Cell>
                  <td />
                  <ShareBar value={r.count} total={total} />
                </tr>
              ))
            )}
            <SectionEnd span={COLS} /><Spacer span={COLS} />

            {/* ── 11–12. Extremes ── */}
            <ExtremesSection num="11" title="Highest Priced Entries" rows={data?.most_expensive ?? []} />
            <ExtremesSection num="12" title="Lowest Priced Entries"  rows={data?.least_expensive ?? []} />

            {/* ── 13. Contributors ── */}
            <SectionHeader num="13" title="Data Contributors" columns={["Contributor", "Records", "", "", "", "Share of records"]} />
            {(data?.contributors ?? []).length === 0 ? <NoDataRow span={COLS} /> : (
              (data?.contributors ?? []).map((r, i) => (
                <tr key={`${r.label}-${i}`} className="border-x border-b border-gray-100 even:bg-gray-50/40 hover:bg-indigo-50/30 transition-colors">
                  <Cell align="left" strong>{r.label}</Cell>
                  <Cell strong>{r.count.toLocaleString()}</Cell>
                  <td colSpan={3} />
                  <ShareBar value={r.count} total={total} />
                </tr>
              ))
            )}
            <SectionEnd span={COLS} /><Spacer span={COLS} />

            {/* ── 14. Coverage gaps ── */}
            <SectionHeader num="14" title="Coverage Gaps — States With No Prices" columns={["State", "", "", "", "", ""]} />
            {(data?.uncovered_states ?? []).length === 0 ? (
              <tr className="border-x border-b border-gray-100 bg-green-50/50">
                <td colSpan={COLS} className="px-4 py-4 text-center text-[12px] font-bold text-green-700">
                  Every state carries at least one price for this selection.
                </td>
              </tr>
            ) : (
              <tr className="border-x border-b border-gray-100">
                <td colSpan={COLS} className="px-4 py-4">
                  <div className="flex flex-wrap gap-1.5">
                    {(data?.uncovered_states ?? []).map((st) => (
                      <span key={st} className="inline-flex px-2 py-0.5 rounded-md bg-red-50 border border-red-200 text-[11px] font-bold text-red-700">
                        {st}
                      </span>
                    ))}
                  </div>
                  <p className="text-[11px] text-gray-400 font-semibold italic mt-2">
                    {(data?.uncovered_states ?? []).length} state
                    {(data?.uncovered_states ?? []).length === 1 ? "" : "s"} with no price record here — where the
                    register needs collecting next.
                  </p>
                </td>
              </tr>
            )}
            <SectionEnd span={COLS} /><Spacer span={COLS} />

          </tbody>
        </table>
      )}

      {/* ── Document footer ── */}
      <div className="mt-10 pt-4 border-t-2 border-gray-300 text-center space-y-1.5">
        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">
          Nigerian Institute of Quantity Surveyors (NIQS) — Confidential
        </p>
        <p className="text-[10px] text-gray-300 font-medium">
          Generated from the NIQS Cost Databank and intended solely for official use.
        </p>
        {s?.last_updated_at && (
          <p className="text-[10px] text-gray-400 font-semibold">
            Register last updated:{" "}
            {new Date(s.last_updated_at).toLocaleString("en-GB", {
              day: "numeric", month: "long", year: "numeric",
            })}
          </p>
        )}
      </div>
    </div>
  );
}
